import { getPermissions, hasPermission } from "../../utilis/permissionStorage";
import React, { useState, useEffect } from "react";
import { apiUrl } from "../ApiManager/AxiosHeaders";
import Loader from "../Loader/Loader";
import { FaEdit, FaEnvelope, FaPhone, FaTrashAlt } from "react-icons/fa";
import CustomPhoneInput from "../Agents/InputField";
import Swal from "sweetalert2";
import { parsePhoneNumber } from "react-phone-number-input";
import { countries_code } from "../../utilis/constants";

const TABLE_COLUMN_COUNT = 9;

const getBranchAgencyName = (row) => row.agencyname || row.name || "—";

const getManagerId = (row) => {
  const id =
    row?.handling_manager ?? row?.handling_manager_id ?? row?.manager ?? "";
  return id != null && id !== "" ? String(id) : "";
};

const getManagerName = (row, userList = []) => {
  const name =
    row?.handling_manager_name ||
    row?.manager_name ||
    row?.agent_manager_name ||
    row?.handling_manager_user;
  if (name) return name;

  const managerId = getManagerId(row);
  if (!managerId) return "—";

  const user = userList.find((item) => String(item?.id) === managerId);
  return user?.name || "—";
};

const formatBranchPhone = (row) => {
  if (row?.country_code && row?.contact) {
    return `${row.country_code} ${row.contact}`;
  }
  return row?.contact || "—";
};

const formatCreatedAt = (value) => {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const buildBranchPhoneValue = (row) => {
  if (!row?.contact) return "";
  if (String(row.contact).startsWith("+")) return row.contact;

  const code = row.country_code
    ? String(row.country_code).replace(/\s/g, "")
    : "";
  if (!code) return row.contact;

  const normalizedCode = code.startsWith("+") ? code : `+${code}`;
  return `${normalizedCode}${row.contact}`;
};

const resolveCountrySelectValue = (storedCountry) => {
  if (!storedCountry) return "";
  const match = countries_code.find(
    (item) => item.value === storedCountry || item.country === storedCountry,
  );
  return match ? match.value : storedCountry;
};

function BranchContactInfo({ row }) {
  const email = row?.email?.trim();
  const phone = formatBranchPhone(row);
  const hasPhone = phone && phone !== "—";

  if (!email && !hasPhone) {
    return <span className="text-muted">—</span>;
  }

  return (
    <div
      className="d-inline-flex align-items-center gap-2"
      style={{ whiteSpace: "nowrap", minWidth: "max-content" }}
    >
      {email && (
        <span
          className="d-inline-flex align-items-center gap-2 px-2 py-1 rounded-pill"
          style={{ backgroundColor: "#eef2ff", color: "#435690" }}
        >
          <FaEnvelope size={11} />
          <span style={{ fontSize: "0.875rem" }}>{email}</span>
        </span>
      )}
      {email && hasPhone && (
        <span className="text-muted" style={{ opacity: 0.45 }}>
          |
        </span>
      )}
      {hasPhone && (
        <span
          className="d-inline-flex align-items-center gap-2 px-2 py-1 rounded-pill"
          style={{ backgroundColor: "#f0fdf4", color: "#166534" }}
        >
          <FaPhone size={11} />
          <span style={{ fontSize: "0.875rem" }}>{phone}</span>
        </span>
      )}
    </div>
  );
}

function BranchFormField({
  label,
  htmlFor,
  required,
  error,
  children,
  className = "col-md-6",
}) {
  return (
    <div className={className}>
      <label htmlFor={htmlFor} className="form-label mb-1 small fw-semibold">
        {label}
        {required && <span className="text-danger ms-1">*</span>}
      </label>
      {children}
      {error && (
        <div className="text-danger" style={{ fontSize: "0.8rem", marginTop: "0.2rem" }}>
          {error}
        </div>
      )}
    </div>
  );
}

function Branches() {
  const permissions = getPermissions();
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editBranch, setEditBranch] = useState(null);
  const [branch, setBranch] = useState({
    name: "",
    agentName: "",
    handlingManager: "",
    email: "",
    contact: "",
    description: "",
    address: "",
    country: "",
  });
  const [branchError, setBranchError] = useState({
    nameError: "",
    agentNameError: "",
    handlingManagerError: "",
    emailError: "",
    contactError: "",
    descriptionError: "",
    addressError: "",
    countryError: "",
  });
  const [userList, setUserList] = useState([]);
  const [loadingButton, setLoadingButton] = useState(false);
  const [viewDetailBranch, setViewDetailBranch] = useState(null);
  const [updatingManagerBranchId, setUpdatingManagerBranchId] = useState(null);

  useEffect(() => {
    fetchBranches();
    fetchUsersForAssigned();
  }, []);

  const fetchUsersForAssigned = async () => {
    try {
      const response = await apiUrl.get("/fetchUsersForAssigned");
      const users = response?.data?.data?.users || [];
      setUserList(users);
    } catch (error) {
      console.error("Error fetching users:", error);
      setUserList([]);
    }
  };
  const fetchBranches = async () => {
    try {
      setLoading(true);
      const response = await apiUrl.get("/getAllBranches");
      if (response?.status) {
        console.log(response?.data?.data);
        setBranches(response?.data?.data);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setBranch((prev) => ({
      ...prev,
      [name]: value,
    }));
    setBranchError((prev) => ({
      ...prev,
      [`${name}Error`]: "",
    }));
  };

  const validateForm = () => {
    let valid = true;
    const newErrors = { ...branchError };

    if (!branch?.name.trim()) {
      newErrors.nameError = "Branch name is required";
      valid = false;
    } else {
      newErrors.nameError = "";
    }

    if (!branch?.agentName.trim()) {
      newErrors.agentNameError = "Agent name is required";
      valid = false;
    } else {
      newErrors.agentNameError = "";
    }

    if (!branch?.handlingManager) {
      newErrors.handlingManagerError = "Please select handling manager";
      valid = false;
    } else {
      newErrors.handlingManagerError = "";
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!branch?.email.trim()) {
      newErrors.emailError = "Email is required";
      valid = false;
    } else if (!emailRegex.test(branch?.email)) {
      newErrors.emailError = "Please enter a valid email";
      valid = false;
    } else {
      newErrors.emailError = "";
    }

    if (!branch?.contact) {
      newErrors.contactError = "Phone number is required";
      valid = false;
    } else {
      newErrors.contactError = "";
    }

    if (!branch?.description.trim()) {
      newErrors.descriptionError = "Description is required";
      valid = false;
    } else {
      newErrors.descriptionError = "";
    }

    if (!branch?.address.trim()) {
      newErrors.addressError = "Address is required";
      valid = false;
    } else {
      newErrors.addressError = "";
    }

    if (!branch?.country) {
      newErrors.countryError = "Please select country";
      valid = false;
    } else {
      newErrors.countryError = "";
    }

    setBranchError(newErrors);
    return valid;
  };
  const saveBranch = async () => {
    if (!validateForm()) return;
    setLoadingButton(true);

    try {
      const loggedInUser = JSON.parse(localStorage.getItem("user") || "{}");
      const handlingManagerId =
        branch.handlingManager || loggedInUser?.id || "";

      const payload = {
        name: branch.name.trim(),
        email: branch.email.trim(),
        description: branch.description.trim(),
        agentname: branch.agentName.trim(),
        handling_manager: handlingManagerId,
        address: branch.address.trim(),
        country: branch.country,
      };

      if (branch.contact) {
        try {
          const phoneNumber = parsePhoneNumber(branch.contact);
          payload.country_code = `+${phoneNumber.countryCallingCode}`;
          payload.contact = phoneNumber.nationalNumber;
        } catch (error) {
          setBranchError((prev) => ({
            ...prev,
            contactError: "Invalid phone number format",
          }));
          setLoadingButton(false);
          return;
        }
      } else {
        setBranchError((prev) => ({
          ...prev,
          contactError: "Phone number is required",
        }));
        setLoadingButton(false);
        return;
      }

      let response;
      if (editBranch) {
        response = await apiUrl.put(`/updateBranch/${editBranch.id}`, payload);
      } else {
        response = await apiUrl.post("/createBranch", payload);
      }
      if (response?.status) {
        Swal.fire({
          title: response?.data?.message,
          icon: "success",
          timer: 3000,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });
        setShowModal(false);
        fetchBranches();
      }
    } catch (error) {
      console.error("Save failed:", error);
    } finally {
      setLoadingButton(false);
    }
  };

  const openCreateModal = () => {
    setBranch({
      name: "",
      agentName: "",
      description: "",
      email: "",
      contact: "",
      handlingManager: "",
      address: "",
      country: "",
    });
    setBranchError({
      nameError: "",
      agentNameError: "",
      handlingManagerError: "",
      emailError: "",
      contactError: "",
      descriptionError: "",
      addressError: "",
      countryError: "",
    });
    setEditBranch(null);
    setShowModal(true);
  };

  const openEditModal = (row) => {
    setEditBranch(row);
    setBranch({
      name: row.name || row.agencyname || "",
      agentName: row.agentname || "",
      handlingManager: getManagerId(row),
      email: row.email || "",
      contact: buildBranchPhoneValue(row),
      description: row.description || "",
      address: row.address || "",
      country: resolveCountrySelectValue(row.country),
    });
    setBranchError({
      nameError: "",
      agentNameError: "",
      handlingManagerError: "",
      emailError: "",
      contactError: "",
      descriptionError: "",
      addressError: "",
      countryError: "",
    });
    setShowModal(true);
  };

  const deleteBranch = async (row) => {
    const confirmation = await Swal.fire({
      title: "Delete branch?",
      text: `Remove "${getBranchAgencyName(row)}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#d33",
    });

    if (!confirmation.isConfirmed) return;

    try {
      const response = await apiUrl.delete(`/deleteBranch/${row.id}`);
      if (response?.status) {
        Swal.fire({
          title: response?.data?.message || "Branch deleted successfully.",
          icon: "success",
          timer: 3000,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });
        fetchBranches();
      }
    } catch (error) {
      Swal.fire(
        "Failed",
        error?.response?.data?.message || "Unable to delete branch.",
        "error",
      );
    }
  };

  const handleManagerChange = async (branchId, handlingManagerId) => {
    if (!handlingManagerId) return;

    setUpdatingManagerBranchId(branchId);
    try {
      const response = await apiUrl.post("changehandlemanager", {
        id: branchId,
        handling_manager: handlingManagerId,
      });

      if (response?.data?.error) {
        Swal.fire("Failed", response.data.error, "error");
        return;
      }

      const selectedUser = userList.find(
        (user) => String(user?.id) === String(handlingManagerId),
      );

      setBranches((prev) =>
        prev.map((row) =>
          String(row.id) === String(branchId)
            ? {
                ...row,
                handling_manager: handlingManagerId,
                handling_manager_name:
                  selectedUser?.name || row.handling_manager_name,
              }
            : row,
        ),
      );

      Swal.fire({
        title: response?.data?.message || "Agent manager updated successfully.",
        icon: "success",
        timer: 2500,
        showConfirmButton: false,
        toast: true,
        position: "top-end",
      });
    } catch (error) {
      Swal.fire(
        "Failed",
        error?.response?.data?.message || "Failed to update agent manager.",
        "error",
      );
    } finally {
      setUpdatingManagerBranchId(null);
    }
  };

  const renderDetailRow = (label, value) => (
    <div className="mb-3">
      <div className="text-muted small mb-1">{label}</div>
      <div className="fw-semibold">{value || "—"}</div>
    </div>
  );
  return (
    <>
      <style>{`
        .branches-table th,
        .branches-table td {
          border: 1px solid #dee2e6 !important;
          vertical-align: middle;
        }
        .branches-table thead th {
          padding: 0.5rem 0.75rem;
          border-bottom: 2px solid #343a40 !important;
        }
        .branches-table tbody td {
          padding: 0.65rem 0.75rem;
        }
        .branches-table tbody tr:hover {
          background-color: #f8fafc;
        }
        .branch-modal-dialog {
          max-width: 920px;
        }
        .branch-modal-body {
          padding: 1.25rem 1.5rem 0.5rem;
        }
        .branch-modal-body .form-control,
        .branch-modal-body .form-select {
          font-size: 0.9rem;
        }
      `}</style>
      <div className="container-fluid px-2 py-3 pb-5">
        <div className="row justify-content-center">
          <div className="flex items-center justify-between px-4">
            {/* heading */}
            <div className="d-flex items-center justify-content-between gap-2">
              <h2 className="mb-0 fw-bold">Branches</h2>
            </div>
            {/* add button for branches */}
            <div className="d-flex justify-content-end">
              {!!permissions.manage_branches && (
                <button onClick={openCreateModal} className="btn btn-primary">
                  Add Branch
                </button>
              )}
            </div>
          </div>

          <div className="mt-3 px-2 mb-5 pb-4">
            <div className="card shadow-sm border-0 mb-3">
              <div className="card-body p-0">
                <div className="table-responsive table-bordered mb-0">
                  <table className="table table-hover align-middle bg-white mb-0 branches-table">
                    <thead className="table-dark">
                      <tr>
                        <th className="text-center" style={{ width: "60px" }}>
                          #
                        </th>
                        <th className="text-center">Agent Name</th>
                        <th className="text-center">Branch Name</th>
                        <th className="text-center">Country</th>
                        <th
                          className="text-center"
                          style={{ minWidth: "280px" }}
                        >
                          Email & Contact
                        </th>
                        <th
                          className="text-center"
                          style={{ minWidth: "180px" }}
                        >
                          Agent Manager
                        </th>
                        <th className="text-center">Created At</th>
                        <th className="text-center">View Detail</th>
                        <th className="text-center" style={{ width: "110px" }}>
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td
                            colSpan={TABLE_COLUMN_COUNT}
                            className="text-center py-5"
                          >
                            <Loader />
                          </td>
                        </tr>
                      ) : branches.length === 0 ? (
                        <tr>
                          <td
                            colSpan={TABLE_COLUMN_COUNT}
                            className="text-center py-5 text-muted"
                          >
                            No branches found
                          </td>
                        </tr>
                      ) : (
                        branches.map((row, index) => (
                          <tr key={row.id || index}>
                            <td className="text-center fw-semibold">
                              {index + 1}
                            </td>
                            <td className="name-text">
                              {row.agentname || "—"}
                            </td>
                            <td>{getBranchAgencyName(row)}</td>
                            <td className="text-center">
                              {row.country || "—"}
                            </td>
                            <td style={{ whiteSpace: "nowrap" }}>
                              <BranchContactInfo row={row} />
                            </td>
                            <td className="text-center">
                              {permissions.manage_branches ? (
                                <select
                                  className="form-select app-form mx-auto"
                                  style={{
                                    minWidth: "160px",
                                    maxWidth: "220px",
                                  }}
                                  value={getManagerId(row)}
                                  disabled={updatingManagerBranchId === row.id}
                                  onChange={(e) =>
                                    handleManagerChange(row.id, e.target.value)
                                  }
                                >
                                  <option value="" disabled>
                                    Please Select
                                  </option>
                                  {userList.map((user) => (
                                    <option key={user?.id} value={user?.id}>
                                      {user?.name}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                getManagerName(row, userList)
                              )}
                            </td>
                            <td className="text-center small text-secondary">
                              {formatCreatedAt(row.created_at)}
                            </td>
                            <td className="text-center">
                              <button
                                type="button"
                                className="btn btn-link btn-sm text-decoration-none p-0"
                                onClick={() => setViewDetailBranch(row)}
                              >
                                View Detail
                              </button>
                            </td>
                            <td className="text-center">
                              {!!permissions.manage_branches && (
                                <div className="d-inline-flex align-items-center gap-2 position-relative">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-primary"
                                    title="Edit branch"
                                    onClick={() => openEditModal(row)}
                                  >
                                    <FaEdit size={13} />
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    title="Delete branch"
                                    onClick={() => deleteBranch(row)}
                                  >
                                    <FaTrashAlt size={13} />
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          {/* modal */}
          {showModal && (
            <>
              {/* Backdrop */}
              <div
                className="modal-backdrop fade show"
                style={{
                  display: "block",
                  backgroundColor: "rgba(0,0,0,0.45)",
                  zIndex: 1040,
                }}
                onClick={() => setShowModal(false)}
              />
              <div
                className="modal fade show d-block"
                tabIndex={-1}
                role="dialog"
                style={{ zIndex: 1050 }}
              >
                <div className="modal-dialog modal-dialog-centered modal-xl branch-modal-dialog">
                  <div
                    className="modal-content border-0 rounded-4 shadow"
                    style={{ overflow: "hidden" }}
                  >
                    <div
                      className="modal-header py-3"
                      style={{
                        background: "#f7f8fa",
                        borderBottom: "1px solid #eef2f6",
                      }}
                    >
                      <div>
                        <h5 className="modal-title mb-0" style={{ fontWeight: 700 }}>
                          {editBranch ? "Edit Branch" : "Add Branch"}
                        </h5>
                        <div className="text-muted" style={{ fontSize: 12 }}>
                          {editBranch
                            ? "Update branch information."
                            : "Please create new branch."}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setShowModal(false)}
                      />
                    </div>

                    <div className="modal-body branch-modal-body">
                      <div className="row g-3">
                        <BranchFormField
                          label="Branch Name"
                          htmlFor="branch_name"
                          required
                          error={branchError.nameError}
                        >
                          <input
                            value={branch.name}
                            type="text"
                            id="branch_name"
                            name="name"
                            className={`form-control ${
                              branchError.nameError ? "is-invalid" : ""
                            }`}
                            placeholder="Aptitude Migration - Sydney"
                            onChange={handleInputChange}
                          />
                        </BranchFormField>

                        <BranchFormField
                          label="Agent Name"
                          htmlFor="agent_name"
                          required
                          error={branchError.agentNameError}
                        >
                          <input
                            value={branch.agentName}
                            type="text"
                            id="agent_name"
                            name="agentName"
                            className={`form-control ${
                              branchError.agentNameError ? "is-invalid" : ""
                            }`}
                            placeholder="Aptitude Migration"
                            onChange={handleInputChange}
                          />
                        </BranchFormField>

                        <BranchFormField
                          label="Handling Manager"
                          htmlFor="handling_manager"
                          required
                          error={branchError.handlingManagerError}
                        >
                          <select
                            value={branch.handlingManager}
                            id="handling_manager"
                            name="handlingManager"
                            className={`form-select ${
                              branchError.handlingManagerError ? "is-invalid" : ""
                            }`}
                            onChange={handleInputChange}
                          >
                            <option value="">Please Select</option>
                            {userList.length > 0 ? (
                              userList.map((user) => (
                                <option key={user?.id} value={user?.id}>
                                  {user?.name}
                                </option>
                              ))
                            ) : (
                              <option disabled>No users available</option>
                            )}
                          </select>
                        </BranchFormField>

                        <BranchFormField
                          label="Email"
                          htmlFor="branch_email"
                          required
                          error={branchError.emailError}
                        >
                          <input
                            value={branch.email}
                            type="text"
                            id="branch_email"
                            name="email"
                            className={`form-control ${
                              branchError.emailError ? "is-invalid" : ""
                            }`}
                            placeholder="info@aptiudemigration.com.au"
                            onChange={handleInputChange}
                          />
                        </BranchFormField>

                        <BranchFormField
                          label="Country"
                          htmlFor="branch_country"
                          required
                          error={branchError.countryError}
                        >
                          <select
                            value={branch.country}
                            id="branch_country"
                            name="country"
                            className={`form-select ${
                              branchError.countryError ? "is-invalid" : ""
                            }`}
                            onChange={handleInputChange}
                          >
                            <option value="">Please Select</option>
                            {countries_code.map((item) => (
                              <option key={item.IsoCode} value={item.value}>
                                {item.value}
                              </option>
                            ))}
                          </select>
                        </BranchFormField>

                        <BranchFormField
                          label="Phone Number"
                          htmlFor="branch_phone"
                          required
                        >
                          <CustomPhoneInput
                            value={branch.contact}
                            onChange={(value) => {
                              setBranch((prev) => ({
                                ...prev,
                                contact: value || "",
                              }));
                              setBranchError((prev) => ({
                                ...prev,
                                contactError: "",
                              }));
                            }}
                            error={branchError.contactError}
                            placeholder="Enter branch phone number"
                            defaultCountry="PK"
                            required={true}
                            label=""
                          />
                        </BranchFormField>

                        <BranchFormField
                          label="Address"
                          htmlFor="branch_address"
                          required
                          error={branchError.addressError}
                          className="col-12"
                        >
                          <textarea
                            rows={2}
                            value={branch.address}
                            id="branch_address"
                            name="address"
                            className={`form-control ${
                              branchError.addressError ? "is-invalid" : ""
                            }`}
                            placeholder="123 Business Street, Sydney NSW 2000"
                            onChange={handleInputChange}
                          />
                        </BranchFormField>

                        <BranchFormField
                          label="Description"
                          htmlFor="branch_description"
                          required
                          error={branchError.descriptionError}
                          className="col-12"
                        >
                          <textarea
                            rows={2}
                            value={branch.description}
                            id="branch_description"
                            name="description"
                            className={`form-control ${
                              branchError.descriptionError ? "is-invalid" : ""
                            }`}
                            placeholder="Branch open in 2024 and deals with migration visa."
                            onChange={handleInputChange}
                          />
                        </BranchFormField>
                      </div>
                    </div>
                    {/* Footer actions */}
                    <div
                      className="modal-footer"
                      style={{
                        background: "#f7f8fa",
                        borderTop: "1px solid #eef2f6",
                      }}
                    >
                      <button
                        className="btn btn-outline-secondary"
                        onClick={() => setShowModal(false)}
                      >
                        Close
                      </button>
                      <button className="btn btn-success" onClick={saveBranch}>
                        {loadingButton ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" />
                            {editBranch ? "Updating" : "Saving"}
                          </>
                        ) : (
                          <>{editBranch ? "Update" : "Save"}</>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {viewDetailBranch && (
            <>
              <div
                className="modal-backdrop fade show"
                style={{
                  display: "block",
                  backgroundColor: "rgba(0,0,0,0.45)",
                  zIndex: 1040,
                }}
                onClick={() => setViewDetailBranch(null)}
              />
              <div
                className="modal fade show d-block"
                tabIndex={-1}
                role="dialog"
                style={{ zIndex: 1050 }}
              >
                <div className="modal-dialog modal-dialog-centered modal-lg">
                  <div className="modal-content border-0 rounded-4 shadow">
                    <div
                      className="modal-header"
                      style={{
                        background: "#f7f8fa",
                        borderBottom: "1px solid #eef2f6",
                      }}
                    >
                      <div>
                        <h5 className="modal-title fw-bold">Branch Detail</h5>
                        <div className="text-muted small">
                          {getBranchAgencyName(viewDetailBranch)}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={() => setViewDetailBranch(null)}
                      />
                    </div>
                    <div className="modal-body">
                      <div className="row">
                        <div className="col-md-6">
                          {renderDetailRow(
                            "Agent Name",
                            viewDetailBranch.agentname,
                          )}
                        </div>
                        <div className="col-md-6">
                          {renderDetailRow(
                            "Agency / Branch Name",
                            getBranchAgencyName(viewDetailBranch),
                          )}
                        </div>
                        <div className="col-md-6">
                          {renderDetailRow("Email", viewDetailBranch.email)}
                        </div>
                        <div className="col-md-6">
                          {renderDetailRow(
                            "Agent Manager",
                            getManagerName(viewDetailBranch, userList),
                          )}
                        </div>
                        <div className="col-md-6">
                          {renderDetailRow(
                            "Phone",
                            formatBranchPhone(viewDetailBranch),
                          )}
                        </div>
                        <div className="col-md-6">
                          {renderDetailRow("Country", viewDetailBranch.country)}
                        </div>
                        <div className="col-12">
                          {renderDetailRow("Address", viewDetailBranch.address)}
                        </div>
                        <div className="col-12">
                          {renderDetailRow(
                            "Description",
                            viewDetailBranch.description,
                          )}
                        </div>
                        <div className="col-md-6">
                          {renderDetailRow(
                            "Created At",
                            formatCreatedAt(viewDetailBranch.created_at),
                          )}
                        </div>
                      </div>
                    </div>
                    <div
                      className="modal-footer"
                      style={{
                        background: "#f7f8fa",
                        borderTop: "1px solid #eef2f6",
                      }}
                    >
                      <button
                        className="btn btn-outline-secondary"
                        onClick={() => setViewDetailBranch(null)}
                      >
                        Close
                      </button>
                      {!!permissions.manage_branches && (
                        <button
                          className="btn btn-primary"
                          onClick={() => {
                            openEditModal(viewDetailBranch);
                            setViewDetailBranch(null);
                          }}
                        >
                          Edit Branch
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default Branches;
